import { Pencil, ChevronLeft, Trash2, Undo2, Redo2 } from 'lucide-react';
import React, { useState, useRef, useEffect, ChangeEvent } from 'react';
import styles from './LBCanvasControls.module.css';
import { WorkflowDetail } from '../../apis/logiblocksAPI';
import { ToggleSwitch } from '@/components';
import { AutoPersistStatusIndicator } from './AutoPersistStatusIndicator';
import type { AutoPersistStatus } from './useAutoPersistWorkflow';

type WorkflowLabelType = 'live' | 'draft' | 'error';
export type LBCanvasTab = 'editor' | 'runs';

function getWorkflowLabelType(
  isDraft: boolean,
  hasValidationErrors: boolean
): WorkflowLabelType {
  if (!isDraft) return 'live';
  if (hasValidationErrors) return 'error';
  return 'draft';
}

const LABEL_STYLES: Record<WorkflowLabelType, string> = {
  live: styles.liveLabel,
  draft: styles.draftLabel,
  error: styles.errorLabel,
};

const LABEL_TEXT: Record<WorkflowLabelType, string> = {
  live: 'Live',
  draft: 'Draft',
  error: 'Errors',
};

function WorkflowLabel({ labelType }: { labelType: WorkflowLabelType }) {
  return (
    <span className={LABEL_STYLES[labelType]}>{LABEL_TEXT[labelType]}</span>
  );
}

interface LBCanvasControlsProps {
  /** The workflow being edited */
  workflow: WorkflowDetail;
  /** Callback fired when the workflow name is changed */
  onChangeWorkflowName?: (workflowId: string, newName: string) => void;
  /** Callback fired when the Back button is clicked */
  onBack?: () => void;
  /** Active canvas tab */
  activeTab?: LBCanvasTab;
  /** Callback fired when the user switches between Editor and Runs */
  onTabChange?: (tab: LBCanvasTab) => void;
  /** Callback fired when the Publish toggle is turned on */
  onPublishWorkflow?: (workflowId: string) => void;
  /** Callback fired when the Publish toggle is turned off */
  onUnpublishWorkflow?: (workflowId: string) => void;
  /** Callback fired when the Delete button is clicked */
  onDeleteWorkflow?: (workflowId: string) => void;
  /** Whether undo is available */
  canUndo?: boolean;
  /** Whether redo is available */
  canRedo?: boolean;
  /** Callback fired when undo button is clicked */
  onUndo?: () => void;
  /** Callback fired when redo button is clicked */
  onRedo?: () => void;
  /** Whether any nodes have validation errors */
  hasValidationErrors?: boolean;
  /** Number of user-visible unresolved fields represented by raw errors. */
  unresolvedFieldCount?: number;
  /** Whether Copilot currently owns the graph mutation lock. */
  isDrafting?: boolean;
  /** Current auto-persist lifecycle state */
  autoPersistStatus?: AutoPersistStatus;
  /** Wall-clock time of the last successful auto-persist */
  autoPersistLastSavedAt?: number | null;
  /** Callback fired when the Test button is clicked. The button shows only when this is set. */
  onTest?: () => void;
  /** Reserve the left side of the toolbar for the full-height Copilot panel. */
  isCopilotOpen?: boolean;
}

export function LBCanvasControls({
  workflow,
  onBack,
  onChangeWorkflowName,
  onPublishWorkflow,
  onUnpublishWorkflow,
  onDeleteWorkflow,
  activeTab = 'editor',
  onTabChange,
  canUndo,
  canRedo,
  onUndo,
  onRedo,
  hasValidationErrors,
  unresolvedFieldCount = 0,
  isDrafting = false,
  autoPersistStatus,
  autoPersistLastSavedAt,
  onTest,
  isCopilotOpen = false,
}: LBCanvasControlsProps) {
  const [isEditingName, setIsEditingName] = useState(false);
  const [editNameValue, setEditNameValue] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  // Focus and select text when entering edit mode
  useEffect(() => {
    if (isEditingName && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditingName]);

  const handleEdit = () => {
    setEditNameValue(workflow.name || '');
    setIsEditingName(true);
  };

  const handleNameSave = () => {
    const trimmedName = editNameValue.trim();
    if (trimmedName.length > 0) {
      onChangeWorkflowName?.(workflow.id, trimmedName);
      setIsEditingName(false);
    }
    // If invalid, stay in edit mode
  };

  const handleNameCancel = () => {
    setIsEditingName(false);
    setEditNameValue('');
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') {
      handleNameCancel();
    } else if (e.key === 'Enter') {
      handleNameSave();
    }
  };

  const handleDelete = () => {
    const workflowName = workflow.name || 'Untitled Workflow';
    const confirmed = window.confirm(
      `Are you sure you want to delete the workflow named ${workflowName}?`
    );
    if (confirmed) {
      onDeleteWorkflow?.(workflow.id);
    }
  };

  const handlePublishToggle = (e: ChangeEvent<HTMLInputElement>) => {
    if (isDrafting) return;
    const isPublishing = e.target.checked;
    if (isPublishing) {
      // Final validation check before publishing
      if (hasValidationErrors) {
        console.error(
          'Cannot publish workflow: validation errors exist in the graph'
        );
        return;
      }
      onPublishWorkflow?.(workflow.id);
    } else {
      onUnpublishWorkflow?.(workflow.id);
    }
  };

  return (
    <div
      className={`${styles.container} ${isCopilotOpen ? styles.withCopilot : ''}`}
    >
      <div className={styles.leftSection}>
        <button
          onClick={onBack}
          className={styles.backButton}
          title='Back to workflow list'
          aria-label='Back to workflow list'
        >
          <ChevronLeft size={20} />
        </button>
        {isEditingName ? (
          <input
            ref={inputRef}
            type='text'
            value={editNameValue}
            onChange={(e) => setEditNameValue(e.target.value)}
            onKeyDown={handleKeyDown}
            onBlur={handleNameSave}
            className={styles.nameInput}
          />
        ) : (
          <>
            <span className={styles.workflowName}>
              {workflow.name || 'Untitled Workflow'}
            </span>
            <button
              onClick={handleEdit}
              className={styles.editNameButton}
              title='Edit workflow name'
              aria-label='Edit workflow name'
            >
              <Pencil size={16} />
            </button>
          </>
        )}
        <div className={styles.tabs} role='tablist' aria-label='Workflow view'>
          <button
            type='button'
            role='tab'
            aria-selected={activeTab === 'editor'}
            className={`${styles.tab} ${
              activeTab === 'editor' ? styles.activeTab : ''
            }`}
            onClick={() => onTabChange?.('editor')}
          >
            Editor
          </button>
          <button
            type='button'
            role='tab'
            aria-selected={activeTab === 'runs'}
            className={`${styles.tab} ${
              activeTab === 'runs' ? styles.activeTab : ''
            }`}
            onClick={() => onTabChange?.('runs')}
            disabled={!workflow.id}
            title={
              !workflow.id ? 'Save the workflow to see its runs' : undefined
            }
          >
            Runs
          </button>
        </div>
      </div>

      <div className={styles.rightSection}>
        <button
          onClick={handleDelete}
          className={styles.deleteButton}
          title='Delete workflow'
          aria-label='Delete workflow'
          disabled={!workflow.id || !workflow.is_draft || isDrafting}
        >
          <Trash2 size={16} />
          <span>Delete</span>
        </button>
        {workflow.is_draft && autoPersistStatus !== undefined && (
          <AutoPersistStatusIndicator
            status={autoPersistStatus}
            lastSavedAt={autoPersistLastSavedAt ?? null}
          />
        )}
        <button
          onClick={onUndo}
          className={styles.iconButton}
          title='Undo (Ctrl+Z)'
          aria-label='Undo'
          disabled={!canUndo || isDrafting}
        >
          <Undo2 size={16} />
        </button>
        <button
          onClick={onRedo}
          className={styles.iconButton}
          title='Redo (Ctrl+Shift+Z)'
          aria-label='Redo'
          disabled={!canRedo || isDrafting}
        >
          <Redo2 size={16} />
        </button>
        <div className={styles.publishToggleContainer}>
          <WorkflowLabel
            labelType={getWorkflowLabelType(
              workflow.is_draft,
              hasValidationErrors ?? false
            )}
          />
          <ToggleSwitch
            checked={!workflow.is_draft}
            onChange={handlePublishToggle}
            labelStyle='publish-toggle-green'
            disabled={
              !workflow.id ||
              isDrafting ||
              (workflow.is_draft && hasValidationErrors)
            }
          />
          {workflow.is_draft && hasValidationErrors && (
            <span
              className={styles.publishBlockedReason}
              role='status'
              title='Complete the highlighted configuration before publishing'
            >
              Resolve {unresolvedFieldCount}{' '}
              {unresolvedFieldCount === 1 ? 'field' : 'fields'} before turning
              on
            </span>
          )}
        </div>
        {onTest && (
          <>
            <div className={styles.headerDivider} />
            <button
              type='button'
              onClick={onTest}
              className={styles.testButton}
              disabled={!workflow.id || isDrafting}
            >
              Test
            </button>
          </>
        )}
      </div>
    </div>
  );
}
